"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import { useCallback, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import BottomNavigation from "../navigation/BottomNavigation";
import { useViewContext } from "../navigation/ViewContext";
import BrainMobileWaves from "../brain/mobile/BrainMobileWaves";
import { useLayout } from "../brain/my-stream/layout/LayoutContext";
import HeaderPlaceholder from "../header/HeaderPlaceholder";
import { useHeaderContext } from "@/contexts/HeaderContext";
import { useDeepLinkNavigation } from "@/hooks/useDeepLinkNavigation";
import BrainMobileMessages from "../brain/mobile/BrainMobileMessages";
import { useSelector } from "react-redux";
import { selectEditingDropId } from "@/store/editSlice";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useAndroidKeyboard } from "@/hooks/useAndroidKeyboard";
import useCapacitor from "@/hooks/useCapacitor";
import PullToRefresh from "../providers/PullToRefresh";
import { getActiveWaveIdFromUrl } from "@/helpers/navigation.helpers";
const TouchDeviceHeader = dynamic(() => import("../header/AppHeader"), {
  ssr: false,
  loading: () => <HeaderPlaceholder />
});
interface Props {
  readonly children: ReactNode;
}
export default function AppLayout(t0) {
  const $ = _c(34);
  const {
    children
  } = t0;
  useDeepLinkNavigation();
  const {
    registerRef
  } = useLayout();
  const {
    setHeaderRef
  } = useHeaderContext();
  const headerRef = useRef(null);
  const {
    activeView
  } = useViewContext();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams?.get("drop");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isSingleDropOpen = t1 !== null;
  let t2;
  if ($[2] !== pathname || $[3] !== searchParams) {
    t2 = getActiveWaveIdFromUrl({
      pathname,
      searchParams
    });
    $[2] = pathname;
    $[3] = searchParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const waveParam = t2;
  let t3;
  if ($[5] !== searchParams) {
    t3 = searchParams?.get("view");
    $[5] = searchParams;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const viewParam = t3;
  const hasWaveParam = Boolean(waveParam);
  const isViewingWavesOrMessages = viewParam === "waves" || viewParam === "messages";
  let t4;
  if ($[7] !== pathname) {
    t4 = pathname === "/waves" || pathname.startsWith("/waves/");
    $[7] = pathname;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isWavesRoute = t4;
  let t5;
  if ($[9] !== pathname) {
    t5 = pathname === "/messages" || pathname.startsWith("/messages/");
    $[9] = pathname;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const isMessagesRoute = t5;
  const isStreamRoute = isWavesRoute || isMessagesRoute || pathname === "/notifications" || pathname === "/" && (hasWaveParam || isViewingWavesOrMessages);
  const editingDropId = useSelector(selectEditingDropId);
  const {
    isApp
  } = useDeviceInfo();
  const {
    isVisible: isAndroidKeyboardVisible,
    isAndroid
  } = useAndroidKeyboard();
  const {
    isIos,
    keyboardVisible: isIosKeyboardVisible
  } = useCapacitor();
  const isEditingOnMobile = isApp && editingDropId !== null;
  const isKeyboardVisible = isAndroid && isAndroidKeyboardVisible || isIos && isIosKeyboardVisible;
  const shouldHideBottomNav = isKeyboardVisible;
  let t6;
  if ($[11] !== registerRef || $[12] !== setHeaderRef) {
    t6 = node => {
      headerRef.current = node;
      registerRef("header", node);
      setHeaderRef(node);
    };
    $[11] = registerRef;
    $[12] = setHeaderRef;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const headerWrapperRef = t6;
  const isNavVisible = !isSingleDropOpen && !isEditingOnMobile && !shouldHideBottomNav;
  const safeAreaClass = !isNavVisible && !isKeyboardVisible ? "tw-pb-[env(safe-area-inset-bottom,0px)]" : "";
  const t7 = `${safeAreaClass} ${"tw-overflow-auto"}`;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PullToRefresh triggerZoneRef={headerRef} />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <TouchDeviceHeader />;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== headerWrapperRef) {
    t10 = <div ref={headerWrapperRef}>{t9}</div>;
    $[16] = headerWrapperRef;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== activeView || $[19] !== children) {
    t11 = activeView === "messages" ? <BrainMobileMessages /> : activeView === "waves" ? <BrainMobileWaves /> : <main>{children}</main>;
    $[18] = activeView;
    $[19] = children;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== isSingleDropOpen || $[22] !== isStreamRoute) {
    t12 = !isSingleDropOpen && !isStreamRoute && <div className="tw-h-[85px] tw-w-full" />;
    $[21] = isSingleDropOpen;
    $[22] = isStreamRoute;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== isEditingOnMobile || $[25] !== isSingleDropOpen || $[26] !== shouldHideBottomNav) {
    t13 = !isSingleDropOpen && !isEditingOnMobile && <BottomNavigation hidden={shouldHideBottomNav} />;
    $[24] = isEditingOnMobile;
    $[25] = isSingleDropOpen;
    $[26] = shouldHideBottomNav;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t13 || $[32] !== t7) {
    t14 = <div className={t7}>{t8}{t10}{t11}{t12}{t13}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t7;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}